// What a freeze was, and what it turned out to be worth.
//
// A freeze keeps a team's score as it stood mid-game. Until now the only
// thing recorded was that number, so a frozen row could say "20" and nothing
// about the game it was taken from. Two things are built here:
//
//   - The snapshot written with the freeze (move_history.detail): the game
//     score, period and clock at that moment, the spread, and the multiplier.
//     Freezes made before this existed carry only { points }, and their game
//     score cannot be recovered: nothing else stored it.
//   - What freezing saved or cost, which needs no snapshot at all: the frozen
//     score against what the team would be on now without it. That works for
//     every freeze, old ones included.
import { explainTeamFantasyPoints, describeMultiplier } from "./scoreBreakdown";
import { formatPeriod, getTeamSpread } from "./gameStatus";

const pts = (n) => `${n} ${Math.abs(n) === 1 ? "pt" : "pts"}`;

/**
 * The detail to store with a freeze. gameInfo is a buildGamesBySlug entry,
 * already from this team's side (homeScore is its own score).
 */
export function buildFreezeSnapshot({ gameInfo, slug, points, isCaptain = false, isTripPlay = false }) {
  const snapshot = { points };
  if (!gameInfo) return snapshot;
  return {
    ...snapshot,
    score: gameInfo.homeScore ?? 0,
    oppScore: gameInfo.awayScore ?? 0,
    period: gameInfo.period ?? null,
    clock: gameInfo.clock ?? null,
    spread: getTeamSpread(gameInfo, slug),
    captain: Boolean(isCaptain),
    tripPlay: Boolean(isTripPlay),
  };
}

// How far either side of level a game is searched when working a freeze
// back from its score. No margin past this changes which scoring tier a team
// is in, so the top and bottom tiers read "or more" rather than a number.
const MARGIN_LIMIT = 80;

/**
 * For a freeze made before snapshots were stored: works the game back from
 * the frozen score. The scoring rules are a short list (win or loss, the
 * underdog bonus, Super Dog, one of nine spread tiers), so a raw score and a
 * spread usually leave exactly one way it could have been made. That gives
 * the breakdown and the range the margin was in, though not the scoreline
 * itself: "leading by 5 to 11", not "38-31".
 *
 * Null when the points do not divide by the multiplier, when no margin
 * produces them, or when more than one kind of game does (a 25 point
 * underdog on 5 could be level, or a few points down with Super Dog
 * cancelling the loss).
 */
export function inferFreezeBreakdown({ frozenPoints, spread, isCaptain = false, isTripPlay = false }) {
  const multiplier = describeMultiplier({ isCaptain, isTripPlay });
  const raw = frozenPoints / (multiplier?.factor || 1);
  if (!Number.isInteger(raw)) return null;

  // Every margin that scores this, grouped by how: the same lines with the
  // same points are one way of making it, whatever the exact margin.
  const ways = new Map();
  for (let margin = -MARGIN_LIMIT; margin <= MARGIN_LIMIT; margin += 1) {
    const b = explainTeamFantasyPoints({ won: margin > 0, tied: margin === 0, spread, actualMargin: margin, isLive: true });
    if (b.total !== raw) continue;
    const key = `${Math.sign(margin)}|${b.lines.map((l) => l.points).join(",")}`;
    const way = ways.get(key) || { min: margin, max: margin, lines: b.lines };
    way.max = margin;
    ways.set(key, way);
  }
  if (ways.size !== 1) return null;

  const [{ min, max, lines }] = [...ways.values()];
  const range = (lo, hi) => (lo === hi ? `${lo}` : `${lo} to ${hi}`);
  const open = (bound) => Math.abs(bound) === MARGIN_LIMIT;
  const scoreLine = min === 0 && max === 0 ? "Level"
    : min > 0 ? (open(max) ? `Leading by ${min} or more` : `Leading by ${range(min, max)}`)
    : (open(min) ? `Trailing by ${-max} or more` : `Trailing by ${range(-max, -min)}`);

  // The spread line is the only one whose wording carries the margin, so it
  // is reworded for the range. Cover is the margin moved by the spread.
  const hasSpread = spread !== null && spread !== undefined;
  const cover = (margin) => (spread < 0 ? margin - Math.abs(spread) : margin + spread);
  const ranged = lines.map((line) => {
    const isSpreadLine = hasSpread && /^(Covering|Missing|On the number)/.test(line.label);
    if (!isSpreadLine || line.points === 0) return line;
    const [lo, hi] = [cover(min), cover(max)].map(Math.abs).sort((a, b) => a - b);
    const verb = line.points > 0 ? "Covering by" : "Missing the spread by";
    const far = line.points > 0 ? open(max) : open(min);
    return { ...line, label: far ? `${verb} ${lo} or more` : `${verb} ${range(lo, hi)}` };
  });

  return { scoreLine, lines: ranged, raw, multiplier, reconciles: true, inferred: true };
}

/**
 * @param {object} args
 * @param {object} [args.detail] move_history.detail for this freeze
 * @param {number} args.frozenPoints the score it was frozen at
 * @param {number|null} args.currentPoints what the team would be on now
 *   unfrozen, multiplier applied, or null when that is not known
 * @param {boolean} args.gameOver whether its game has finished
 * @param {number|null} [args.spread] the team's spread, and
 * @param {boolean} [args.isCaptain]
 * @param {boolean} [args.isTripPlay] for working an older freeze back from
 *   its score when no snapshot was stored with it
 * @returns {{
 *   at: { scoreLine: string, lines: {label, points}[], raw: number,
 *         multiplier: {label, factor}|null, reconciles: boolean } | null,
 *   verdict: { text: string, tone: "good"|"bad"|"even" } | null,
 * }}
 */
export function describeFreeze({ detail, frozenPoints, currentPoints, gameOver, spread, isCaptain, isTripPlay }) {
  let at = null;
  if (detail && typeof detail.score === "number" && typeof detail.oppScore === "number") {
    const margin = detail.score - detail.oppScore;
    const breakdown = explainTeamFantasyPoints({
      won: margin > 0,
      tied: margin === 0,
      spread: detail.spread,
      actualMargin: margin,
      // "Winning", not "Win": this is a score part way through a game.
      isLive: true,
    });
    const multiplier = describeMultiplier({ isCaptain: detail.captain, isTripPlay: detail.tripPlay });
    const when = [formatPeriod(detail.period), detail.clock && detail.clock !== "0:00" ? detail.clock : null]
      .filter(Boolean).join(" ");
    const standing = margin > 0 ? "Leading" : margin < 0 ? "Trailing" : "Level at";
    at = {
      scoreLine: `${standing} ${detail.score}-${detail.oppScore}${when ? `, ${when}` : ""}`,
      lines: breakdown.lines,
      raw: breakdown.total,
      multiplier,
      // The frozen number comes from the last scoring update, the scoreline
      // from the last scoreboard poll. They can be a play apart, in which
      // case the sums below will not land on the frozen score, and the panel
      // says so rather than showing arithmetic that does not add up.
      reconciles: breakdown.total * (multiplier?.factor || 1) === frozenPoints,
    };
  }

  if (!at && typeof frozenPoints === "number") {
    at = inferFreezeBreakdown({ frozenPoints, spread, isCaptain, isTripPlay });
  }

  let verdict = null;
  if (typeof currentPoints === "number" && typeof frozenPoints === "number") {
    const diff = frozenPoints - currentPoints;
    const tone = diff > 0 ? "good" : diff < 0 ? "bad" : "even";
    if (gameOver) {
      verdict = {
        tone,
        text: diff > 0 ? `Freezing saved ${pts(diff)}. Unfrozen, it would have finished on ${currentPoints}.`
          : diff < 0 ? `Freezing cost ${pts(-diff)}. Unfrozen, it would have finished on ${currentPoints}.`
          : `Freezing made no difference. It finished on ${currentPoints} either way.`,
      };
    } else {
      verdict = {
        tone,
        text: diff > 0 ? `The freeze is ${pts(diff)} ahead right now. Unfrozen, it would be on ${currentPoints}.`
          : diff < 0 ? `The freeze is ${pts(-diff)} behind right now. Unfrozen, it would be on ${currentPoints}.`
          : `No difference right now. Unfrozen, it would also be on ${currentPoints}.`,
      };
    }
  }

  return { at, verdict };
}
